import React from 'react';
import {
  Box, FormControlLabel, TextField, Typography, Paper,
  RadioGroup, Radio, Switch, Tooltip, Select, MenuItem, FormLabel,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';

interface HomeSettingsProps {
  platform: string;
  executionMode: string;

  targetVersion?: string;

  // Moved from BasicInformation (only shown in analyze/fixups/deploy - not create_home)
  oracleBaseEnabled: boolean;
  setOracleBaseEnabled: (v: boolean) => void;
  oracleBase: string;
  setOracleBase: (v: string) => void;
  editionEnabled: boolean;
  setEditionEnabled: (v: boolean) => void;
  edition: string;
  setEdition: (v: string) => void;
  oraInventoryEnabled: boolean;
  setOraInventoryEnabled: (v: boolean) => void;
  oraInventory: string;
  setOraInventory: (v: string) => void;
  inventoryGroupEnabled: boolean;
  setInventoryGroupEnabled: (v: boolean) => void;
  inventoryGroup: string;
  setInventoryGroup: (v: string) => void;
  ignorePrereqFailure: boolean;
  setIgnorePrereqFailure: (v: boolean) => void;

  // Moved from AdditionalOptions RAC section
  binaryOptionRac: boolean | undefined;
  setBinaryOptionRac: (v: boolean | undefined) => void;
  clusterNodesEnabled: boolean;
  setClusterNodesEnabled: (v: boolean) => void;
  clusterNodes: string;
  setClusterNodes: (v: string) => void;

  // New Home Settings fields
  hsAccountType: string;
  setHsAccountType: (v: string) => void;
  hsBinoptAsm: boolean | undefined;
  setHsBinoptAsm: (v: boolean | undefined) => void;
  hsBinoptDnfs: boolean | undefined;
  setHsBinoptDnfs: (v: boolean | undefined) => void;
  hsBinoptDm: boolean | undefined;
  setHsBinoptDm: (v: boolean | undefined) => void;
  hsBinoptJox: boolean | undefined;
  setHsBinoptJox: (v: boolean | undefined) => void;
  hsBinoptOlap: boolean | undefined;
  setHsBinoptOlap: (v: boolean | undefined) => void;
  hsBinoptPart: boolean | undefined;
  setHsBinoptPart: (v: boolean | undefined) => void;
  hsBinoptRat: boolean | undefined;
  setHsBinoptRat: (v: boolean | undefined) => void;
  hsBinoptSdo: boolean | undefined;
  setHsBinoptSdo: (v: boolean | undefined) => void;
  hsBinoptUniaud: boolean | undefined;
  setHsBinoptUniaud: (v: boolean | undefined) => void;
  hsHomeName: string;
  setHsHomeName: (v: string) => void;
  hsIgnoreOpatchConflict: 'error' | 'keep_first' | 'skip_all';
  setHsIgnoreOpatchConflict: (v: 'error' | 'keep_first' | 'skip_all') => void;
  hsOsdbaGroup: string;
  setHsOsdbaGroup: (v: string) => void;
  hsOsoperGroup: string;
  setHsOsoperGroup: (v: string) => void;
  hsOsbackupdbaGroup: string;
  setHsOsbackupdbaGroup: (v: string) => void;
  hsOsdgdbaGroup: string;
  setHsOsdgdbaGroup: (v: string) => void;
  hsOskmdbaGroup: string;
  setHsOskmdbaGroup: (v: string) => void;
  hsOsracdbaGroup: string;
  setHsOsracdbaGroup: (v: string) => void;
  hsReadOnly: boolean;
  setHsReadOnly: (v: boolean) => void;
  hsRuApply: boolean;
  setHsRuApply: (v: boolean) => void;
  hsEnvTmp: string;
  setHsEnvTmp: (v: string) => void;
}

const tooltipSx = {
  bgcolor: (theme: any) => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
  color: (theme: any) => theme.palette.mode === 'dark' ? '#222' : '#fff',
  fontSize: 15,
  borderRadius: 2,
  boxShadow: 3,
  px: 2,
  py: 1,
  fontWeight: 400,
  minWidth: 280,
};

const SwitchRow: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  tooltip?: string;
  disabled?: boolean;
}> = ({ checked, onChange, label, tooltip, disabled }) => {
  const control = (
    <FormControlLabel
      sx={{ width: 'fit-content' }}
      control={<Switch checked={checked} onChange={e => onChange(e.target.checked)} color="primary" size="small" disabled={disabled} />}
      label={<Typography sx={{ fontWeight: 600, fontSize: 15, opacity: disabled ? 0.5 : 1 }}>{label}</Typography>}
    />
  );
  if (!tooltip) return control;
  return (
    <Tooltip
      title={tooltip}
      placement="right"
      arrow
      componentsProps={{ tooltip: { sx: tooltipSx } }}
    >
      {control}
    </Tooltip>
  );
};

const tbBtnSx = {
  py: 0.25,
  px: 1.2,
  fontSize: 12,
  minWidth: 40,
  '&.Mui-selected': {
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    '&:hover': { bgcolor: 'primary.dark' },
  },
};

const TriStateRow: React.FC<{
  value: boolean | undefined;
  onChange: (v: boolean | undefined) => void;
  label: string;
  tooltip?: string;
}> = ({ value, onChange, label, tooltip }) => {
  const tbVal = value === true ? 'yes' : value === false ? 'no' : 'unset';
  const handleChange = (_: React.MouseEvent, v: string | null) => {
    if (v === 'yes') onChange(true);
    else if (v === 'no') onChange(false);
    else onChange(undefined);
  };
  const row = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      <Typography sx={{ fontWeight: 600, flex: 1 }}>{label}</Typography>
      <ToggleButtonGroup size="small" exclusive value={tbVal} onChange={handleChange}>
        <ToggleButton value="yes" sx={tbBtnSx}>ON</ToggleButton>
        <ToggleButton value="no" sx={tbBtnSx}>OFF</ToggleButton>
        <ToggleButton value="unset" sx={{ ...tbBtnSx, minWidth: 56 }}>UNSET</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
  if (!tooltip) return row;
  return (
    <Tooltip title={tooltip} placement="top" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
      {row}
    </Tooltip>
  );
};

const HomeSettings: React.FC<HomeSettingsProps> = ({
  platform,
  executionMode,
  targetVersion,
  // moved from BasicInfo
  oracleBaseEnabled, setOracleBaseEnabled,
  oracleBase, setOracleBase,
  editionEnabled, setEditionEnabled,
  edition, setEdition,
  oraInventoryEnabled, setOraInventoryEnabled,
  oraInventory, setOraInventory,
  inventoryGroupEnabled, setInventoryGroupEnabled,
  inventoryGroup, setInventoryGroup,
  ignorePrereqFailure, setIgnorePrereqFailure,
  // moved from AdditionalOptions
  binaryOptionRac, setBinaryOptionRac,
  clusterNodesEnabled, setClusterNodesEnabled,
  clusterNodes, setClusterNodes,
  // new fields
  hsAccountType, setHsAccountType,
  hsBinoptAsm, setHsBinoptAsm,
  hsBinoptDnfs, setHsBinoptDnfs,
  hsBinoptDm, setHsBinoptDm,
  hsBinoptJox, setHsBinoptJox,
  hsBinoptOlap, setHsBinoptOlap,
  hsBinoptPart, setHsBinoptPart,
  hsBinoptRat, setHsBinoptRat,
  hsBinoptSdo, setHsBinoptSdo,
  hsBinoptUniaud, setHsBinoptUniaud,
  hsHomeName, setHsHomeName,
  hsIgnoreOpatchConflict, setHsIgnoreOpatchConflict,
  hsOsdbaGroup, setHsOsdbaGroup,
  hsOsoperGroup, setHsOsoperGroup,
  hsOsbackupdbaGroup, setHsOsbackupdbaGroup,
  hsOsdgdbaGroup, setHsOsdgdbaGroup,
  hsOskmdbaGroup, setHsOskmdbaGroup,
  hsOsracdbaGroup, setHsOsracdbaGroup,
  hsReadOnly, setHsReadOnly,
  hsRuApply, setHsRuApply,
  hsEnvTmp, setHsEnvTmp,
}) => {
  const isWindows = platform === 'WINDOWS.X64';
  const isCreateHome = executionMode === 'create_home';

  // account_type UI state
  const deriveAccountMode = (val: string) => {
    if (!val) return '';
    if (val === 'VIRTUAL') return 'VIRTUAL';
    if (val === 'BUILT_IN') return 'BUILT_IN';
    if (val.startsWith('USER:')) return 'DOMAIN';
    return 'LOCAL';
  };
  const [accountMode, setAccountMode] = React.useState<string>(() => deriveAccountMode(hsAccountType));
  const [accountCustom, setAccountCustom] = React.useState<string>(() => {
    if (hsAccountType === 'VIRTUAL' || hsAccountType === 'BUILT_IN') return '';
    if (hsAccountType.startsWith('USER:')) return hsAccountType.slice(5);
    return hsAccountType;
  });

  const handleAccountModeChange = (mode: string) => {
    setAccountMode(mode);
    if (mode === 'VIRTUAL') { setHsAccountType('VIRTUAL'); setAccountCustom(''); }
    else if (mode === 'BUILT_IN') { setHsAccountType('BUILT_IN'); setAccountCustom(''); }
    else if (mode === '') { setHsAccountType(''); setAccountCustom(''); }
    else {
      // LOCAL or DOMAIN — keep current custom text
      if (mode === 'DOMAIN') setHsAccountType(accountCustom ? `USER:${accountCustom}` : '');
      else setHsAccountType(accountCustom);
    }
  };

  const handleAccountCustomChange = (val: string) => {
    setAccountCustom(val);
    if (accountMode === 'DOMAIN') setHsAccountType(val ? `USER:${val}` : '');
    else setHsAccountType(val);
  };

  // Reset cluster nodes when RAC is turned off
  React.useEffect(() => {
    if (binaryOptionRac !== true) {
      setClusterNodesEnabled(false);
    }
  }, [binaryOptionRac, setClusterNodesEnabled]);

  // Oracle base touched
  const [oracleBaseTouched, setOracleBaseTouched] = React.useState(false);
  React.useEffect(() => { setOracleBaseTouched(false); }, [platform]);

  const textFieldSx = {
    '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
    '& .MuiInputBase-input': { fontSize: 15, background: 'transparent' },
  };

  // Shared text field renderer
  const renderTextField = (
    value: string,
    onChange: (v: string) => void,
    placeholder: string,
    touched?: boolean,
    onFocus?: () => void,
  ) => (
    <TextField
      fullWidth
      size="small"
      value={value}
      onChange={e => onChange(e.target.value)}
      onFocus={onFocus}
      placeholder={placeholder}
      sx={{ mt: 0.5, mb: 1 }}
      InputProps={{ sx: textFieldSx }}
    />
  );

  return (
    <Paper elevation={0} sx={{ p: 2, background: 'background.paper', borderRadius: 3 }}>

      {/* ── account_type (Windows only) ─────────────────────────── */}
      {isWindows && (
        <Box sx={{ mb: 3 }}>
          <Paper elevation={0} sx={{ p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: theme => theme.palette.primary.main }}>
              Oracle Home User (Windows)
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>
              On Microsoft Windows, select the Oracle Home User type:
            </Typography>
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 0.5, display: 'block', fontSize: 14 }}>Account Type</FormLabel>
            <Select
              size="small"
              value={accountMode}
              onChange={e => handleAccountModeChange(e.target.value)}
              displayEmpty
              sx={{ minWidth: 320, mb: 1.5 }}
            >
              <MenuItem value=""><em>— Not set —</em></MenuItem>
              <MenuItem value="VIRTUAL">Windows Virtual Account (VIRTUAL)</MenuItem>
              <MenuItem value="BUILT_IN">Windows Built-in Account (BUILT_IN)</MenuItem>
              <MenuItem value="LOCAL">Standard Windows user account</MenuItem>
              <MenuItem value="DOMAIN">Domain user account (USER:domain\user)</MenuItem>
            </Select>
            {(accountMode === 'LOCAL') && (
              <TextField
                size="small"
                fullWidth
                value={accountCustom}
                onChange={e => handleAccountCustomChange(e.target.value)}
                placeholder="myWindowsUser"
                label="Username"
                sx={{ mt: 0.5 }}
                InputProps={{ sx: textFieldSx }}
              />
            )}
            {(accountMode === 'DOMAIN') && (
              <TextField
                size="small"
                fullWidth
                value={accountCustom}
                onChange={e => handleAccountCustomChange(e.target.value)}
                placeholder="CORP\jdoe"
                label="Domain\User"
                sx={{ mt: 0.5 }}
                InputProps={{ sx: textFieldSx }}
              />
            )}
          </Paper>
        </Box>
      )}

      {/* ── Two-column general section ───────────────────────────── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, columnGap: 4, alignItems: 'flex-start', mb: 3 }}>

        {/* LEFT column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 220, flex: 1 }}>

          {/* Fields moved from BasicInformation — shown only in analyze/fixups/deploy */}
          {!isCreateHome && (
            <>
              {/* Oracle Base */}
              <Tooltip title="Specifies the directory that should be used for the Oracle base directory." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
                <FormControlLabel
                  sx={{ width: 'fit-content' }}
                  control={<Switch checked={oracleBaseEnabled} onChange={e => setOracleBaseEnabled(e.target.checked)} color="primary" size="small" />}
                  label={<Typography sx={{ fontWeight: 600, fontSize: 15 }}>Oracle Base</Typography>}
                />
              </Tooltip>
              {oracleBaseEnabled && renderTextField(
                oracleBase,
                v => { setOracleBaseTouched(true); setOracleBase(v); },
                isWindows ? 'C:\\Oracle' : '/u01/app/oracle',
                oracleBaseTouched,
              )}

              {/* Edition */}
              <Tooltip title="Specifies the Oracle Database edition for the target ORACLE_HOME." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
                <FormControlLabel
                  sx={{ width: 'fit-content' }}
                  control={<Switch checked={editionEnabled} onChange={e => setEditionEnabled(e.target.checked)} color="primary" size="small" />}
                  label={<Typography sx={{ fontWeight: 600, fontSize: 15 }}>Edition</Typography>}
                />
              </Tooltip>
              {editionEnabled && (
                <RadioGroup row value={edition} onChange={e => setEdition(e.target.value)} sx={{ ml: 1, mb: 0.5 }}>
                  <FormControlLabel value="se2" disabled={targetVersion === '23' || targetVersion === '26'} control={<Radio size="small" />} label="Standard Edition 2" />
                  <FormControlLabel value="ee" control={<Radio size="small" />} label="Enterprise Edition" />
                </RadioGroup>
              )}

              {/* oraInventory + Inventory Group (Linux only) */}
              {!isWindows && (
                <>
                  <Tooltip title="Specifies the Oracle Database inventory (oraInventory) directory. Required when creating a new Oracle Home on a machine with no existing Oracle Inventory." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
                    <FormControlLabel
                      sx={{ width: 'fit-content' }}
                      control={<Switch checked={oraInventoryEnabled} onChange={e => setOraInventoryEnabled(e.target.checked)} color="primary" size="small" />}
                      label={<Typography sx={{ fontWeight: 600, fontSize: 15 }}>Inventory Location</Typography>}
                    />
                  </Tooltip>
                  {oraInventoryEnabled && renderTextField(oraInventory, setOraInventory, '/u01/app/oraInventory')}

                  <Tooltip title="Members of the Oracle Inventory group are granted OINSTALL privileges to read and write to the Oracle Inventory directory." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
                    <FormControlLabel
                      sx={{ width: 'fit-content' }}
                      control={<Switch checked={inventoryGroupEnabled} onChange={e => setInventoryGroupEnabled(e.target.checked)} color="primary" size="small" />}
                      label={<Typography sx={{ fontWeight: 600, fontSize: 15 }}>Inventory Group</Typography>}
                    />
                  </Tooltip>
                  {inventoryGroupEnabled && renderTextField(inventoryGroup, setInventoryGroup, 'oinstall')}
                </>
              )}

              {/* Ignore PreReq Failure */}
              <SwitchRow
                checked={ignorePrereqFailure}
                onChange={setIgnorePrereqFailure}
                label="Ignore PreReq Failure"
                tooltip="When enabled, AutoUpgrade will ignore prerequisite check failures."
              />
            </>
          )}

          {/* home_name (Linux only) */}
          {!isWindows && (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5 }}>Home Name</Typography>
              <TextField
                fullWidth
                size="small"
                value={hsHomeName}
                onChange={e => setHsHomeName(e.target.value)}
                placeholder="OraDB19Home1"
                InputProps={{ sx: textFieldSx }}
              />
            </Box>
          )}

          {/* ignore_opatch_conflict — Windows only; on non-Windows it moves to the right column */}
          {isWindows && (
            <Box>
              <Tooltip
                title={
                  <Box>
                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>ignore_opatch_conflict</Typography>
                    <Typography sx={{ fontSize: 14 }}><b>error</b> (default): Stop with error if a conflict is detected.</Typography>
                    <Typography sx={{ fontSize: 14 }}><b>keep_first</b>: Keep the first patch and ignore the conflict.</Typography>
                    <Typography sx={{ fontSize: 14 }}><b>skip_all</b>: Skip all conflicting patches.</Typography>
                  </Box>
                }
                placement="right"
                arrow
                componentsProps={{ tooltip: { sx: tooltipSx } }}
              >
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 0.5, display: 'block', fontSize: 15, cursor: 'help' }}>
                  OPatch Conflict Handling
                </FormLabel>
              </Tooltip>
              <Select
                size="small"
                value={hsIgnoreOpatchConflict}
                onChange={e => setHsIgnoreOpatchConflict(e.target.value as 'error' | 'keep_first' | 'skip_all')}
                sx={{ minWidth: 180, '& .MuiSelect-select': { color: hsIgnoreOpatchConflict === 'error' ? '#8a8a8a' : undefined } }}
              >
                <MenuItem value="error">Error (default)</MenuItem>
                <MenuItem value="keep_first">Keep First</MenuItem>
                <MenuItem value="skip_all">Skip All</MenuItem>
              </Select>
            </Box>
          )}
        </Box>

        {/* RIGHT column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 220, flex: 1, justifyContent: 'flex-end', alignSelf: 'stretch' }}>

          {/* read_only */}
          <SwitchRow
            checked={hsReadOnly}
            onChange={setHsReadOnly}
            label="Read-Only Home"
            tooltip="Configures the Oracle Home as a read-only home."
          />

          {/* ru_apply — locked ON when RAC=ON (implicit default, not written to config) */}
          <SwitchRow
            checked={hsRuApply || binaryOptionRac === true}
            onChange={v => setHsRuApply(v)}
            disabled={binaryOptionRac === true}
            label="Apply RU (ru_apply)"
            tooltip="Specifies whether a release update (RU) is installed at the same time as the ORACLE_HOME, or installed as a separate step by OPatch. The default value is no, unless the current operating system is Oracle Linux 9 or higher, or RAC is enabled."
          />

          {/* env=TMP */}
          <Box sx={isWindows ? { mt: 1.5 } : {}}>
            <Tooltip
              title="Sets the TMP environment variable used during patching operations. Example: /u02/tmp"
              placement="right"
              arrow
              componentsProps={{ tooltip: { sx: tooltipSx } }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5, cursor: 'help' }}>
                TMP Directory (env=TMP)
              </Typography>
            </Tooltip>
            <TextField
              fullWidth
              size="small"
              value={hsEnvTmp}
              onChange={e => setHsEnvTmp(e.target.value)}
              placeholder={isWindows ? 'C:\\Temp' : '/u02/tmp'}
              InputProps={{ sx: textFieldSx }}
            />
          </Box>

          {/* ignore_opatch_conflict — non-Windows only; on Windows it stays in the left column */}
          {!isWindows && (
            <Box>
              <Tooltip
                title={
                  <Box>
                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>ignore_opatch_conflict</Typography>
                    <Typography sx={{ fontSize: 14 }}><b>error</b> (default): Stop with error if a conflict is detected.</Typography>
                    <Typography sx={{ fontSize: 14 }}><b>keep_first</b>: Keep the first patch and ignore the conflict.</Typography>
                    <Typography sx={{ fontSize: 14 }}><b>skip_all</b>: Skip all conflicting patches.</Typography>
                  </Box>
                }
                placement="right"
                arrow
                componentsProps={{ tooltip: { sx: tooltipSx } }}
              >
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 0.5, display: 'block', fontSize: 15, cursor: 'help' }}>
                  OPatch Conflict Handling
                </FormLabel>
              </Tooltip>
              <Select
                fullWidth
                size="small"
                value={hsIgnoreOpatchConflict}
                onChange={e => setHsIgnoreOpatchConflict(e.target.value as 'error' | 'keep_first' | 'skip_all')}
                sx={{ '& .MuiSelect-select': { color: hsIgnoreOpatchConflict === 'error' ? '#8a8a8a' : undefined } }}
              >
                <MenuItem value="error">Error (default)</MenuItem>
                <MenuItem value="keep_first">Keep First</MenuItem>
                <MenuItem value="skip_all">Skip All</MenuItem>
              </Select>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Binary Options (switches) ─────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Paper elevation={0} sx={{ p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: theme => theme.palette.primary.main }}>
            Binary Options
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
            <b>Unset</b> follows the Oracle DB installation defaults (most options are enabled by default).
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 220 }}>
              <TriStateRow value={binaryOptionRac} onChange={setBinaryOptionRac} label="RAC" tooltip="Oracle Real Application Clusters (RAC) binary option." />
              {binaryOptionRac === true && (
                <Box sx={{ ml: 2 }}>
                  <SwitchRow
                    checked={clusterNodesEnabled}
                    onChange={setClusterNodesEnabled}
                    label="Cluster Nodes"
                    tooltip="Specifies the list of nodes in an Oracle RAC cluster on which you want to run AutoUpgrade."
                  />
                  {clusterNodesEnabled && (
                    <TextField
                      fullWidth
                      size="small"
                      value={clusterNodes}
                      onChange={e => setClusterNodes(e.target.value)}
                      placeholder="node1,node2,node3"
                      sx={{ mt: 0.5, mb: 0.5 }}
                      InputProps={{ sx: textFieldSx }}
                    />
                  )}
                </Box>
              )}
              {!isWindows && <TriStateRow value={hsBinoptAsm} onChange={setHsBinoptAsm} label="ASM" tooltip="Oracle Automatic Storage Management (ASM) binary option." />}
              {!isWindows && <TriStateRow value={hsBinoptDm} onChange={setHsBinoptDm} label="Data Mining (DM)" tooltip="Data Mining (DM) binary option." />}
              <TriStateRow value={hsBinoptDnfs} onChange={setHsBinoptDnfs} label="dNFS" tooltip="Direct NFS (dNFS) binary option." />
              <TriStateRow value={hsBinoptOlap} onChange={setHsBinoptOlap} label="OLAP" tooltip="Online Analytical Processing (OLAP) binary option." />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 220 }}>
              {!isWindows && <TriStateRow value={hsBinoptJox} onChange={setHsBinoptJox} label="JOX" tooltip="Java Object eXtensibility (JOX) binary option." />}
              <TriStateRow value={hsBinoptPart} onChange={setHsBinoptPart} label="Partitioning" tooltip="Partitioning binary option." />
              <TriStateRow value={hsBinoptRat} onChange={setHsBinoptRat} label="RAT" tooltip="Real Application Testing (RAT) binary option." />
              {!isWindows && <TriStateRow value={hsBinoptSdo} onChange={setHsBinoptSdo} label="SDO" tooltip="Oracle Spatial and Graph (SDO) binary option." />}
              <TriStateRow value={hsBinoptUniaud} onChange={setHsBinoptUniaud} label="Unified Auditing" tooltip="Unified Auditing binary option." />
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* ── OS Groups (Linux only) ─────────────────────────────────── */}
      {!isWindows && (
        <Box>
          <Paper elevation={0} sx={{ p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: theme => theme.palette.primary.main }}>
              OS Groups
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>
              Only add to generated config if you provide a value. Default placeholder: <b>dba</b>
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, columnGap: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200, flex: 1 }}>
                <Box>
                  <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5 }}>OSDBA Group</Typography>
                  <TextField size="small" fullWidth value={hsOsdbaGroup} onChange={e => setHsOsdbaGroup(e.target.value)} placeholder="dba" InputProps={{ sx: textFieldSx }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5 }}>OSOPER Group</Typography>
                  <TextField size="small" fullWidth value={hsOsoperGroup} onChange={e => setHsOsoperGroup(e.target.value)} placeholder="dba" InputProps={{ sx: textFieldSx }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5 }}>OSBACKUPDBA Group</Typography>
                  <TextField size="small" fullWidth value={hsOsbackupdbaGroup} onChange={e => setHsOsbackupdbaGroup(e.target.value)} placeholder="dba" InputProps={{ sx: textFieldSx }} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200, flex: 1 }}>
                <Box>
                  <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5 }}>OSDGDBA Group</Typography>
                  <TextField size="small" fullWidth value={hsOsdgdbaGroup} onChange={e => setHsOsdgdbaGroup(e.target.value)} placeholder="dba" InputProps={{ sx: textFieldSx }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5 }}>OSKMDBA Group</Typography>
                  <TextField size="small" fullWidth value={hsOskmdbaGroup} onChange={e => setHsOskmdbaGroup(e.target.value)} placeholder="dba" InputProps={{ sx: textFieldSx }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5 }}>OSRACDBA Group</Typography>
                  <TextField size="small" fullWidth value={hsOsracdbaGroup} onChange={e => setHsOsracdbaGroup(e.target.value)} placeholder="dba" InputProps={{ sx: textFieldSx }} />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Paper>
  );
};

export default HomeSettings;
